
import { useState, useEffect } from "react";
import axios from "axios";
import { Users, Upload, FileSpreadsheet, UserPlus } from "lucide-react";
import * as XLSX from "xlsx";

// Reusable local field component matching inline structures
const LocalField = ({ label, placeholder, select, options, required, value, onChange }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "4px", width: "100%" }}>
    <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>
      {label} {required && <span style={{ color: "#ef4444" }}>*</span>}
    </label>
    {select ? (
      <select
        value={value}
        onChange={onChange}
        style={{ padding: "8px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px", backgroundColor: "#fff" }}
      >
        <option value="">Select an option</option>
        {options?.map((opt, i) => {
          const val = typeof opt === "object" ? (opt.name || opt.table_name || opt.id) : opt;
          const boundValue = typeof opt === "object" ? (opt.id || opt.table_name || opt.name) : opt;
          return <option key={i} value={boundValue}>{val}</option>;
        })}
      </select>
    ) : (
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        style={{ padding: "8px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px" }}
      />
    )}
  </div>
);

export default function GroupContactManager() {
  // Navigation Mode: 'single' or 'bulk'
  const [uploadMode, setUploadMode] = useState("single");

  // Shared Form Configuration State
  const [selectedTable, setSelectedTable] = useState("");
  const [selectedGroup2, setSelectedGroup2] = useState("");
  const [selectedSubgroup1, setSelectedSubgroup1] = useState("");

  // Single Form Inputs
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");

  // Bulk File Storage
  const [selectedFile, setSelectedFile] = useState(null);

  // Core Data Lists
  const [contacts, setContacts] = useState([]);
  const [groups2, setGroups2] = useState([]);
  const [subgroups1, setSubgroups1] = useState([]);
  const [tableOptions, setTableOptions] = useState([]);
  const [tableColumns, setTableColumns] = useState([]);

  const fetchGroup2 = () => {
    const user = JSON.parse(sessionStorage.getItem('user'));
    if (!user) return;
    const emailToUse = user.role === "admin" ? user.id : user.userId;
    axios.get(`https://botapi.emedha.in/pages/api/apiwer/groups?user_id=${emailToUse}`)
      .then(response => {
        const groupsData = response.data;
        let filteredGroups;
        if (user.role === "admin") {
          filteredGroups = groupsData;
        } else if (user.role === "member") {
          filteredGroups = groupsData.filter(group => group.role === "member" && group.member_id === user.id);
        }
        setGroups2(filteredGroups);
      })
      .catch(error => console.error('Error fetching groups context arrays:', error));
  };

  const fetchSubGroups1 = () => {
    const user = JSON.parse(sessionStorage.getItem('user'));
    if (!user) return;
    const emailToUse = user.role === "admin" ? user.id : user.userId;
    axios.get(`https://botapi.emedha.in/pages/api/api/subgroups?user_id=${emailToUse}`)
      .then(response => {
        const groupsData = response.data;
        let filteredGroups;
        if (user.role === "admin") {
          filteredGroups = groupsData;
        } else if (user.role === "member") {
          filteredGroups = groupsData.filter(group => group.role === "member" && group.member_id === user.id);
        }
        setSubgroups1(filteredGroups);
      })
      .catch(error => console.error('Error fetching subgroups sync contexts:', error));
  };

  // Initial Sync Pipeline
  useEffect(() => {
    fetchGroup2();
    fetchSubGroups1();

    const user = JSON.parse(sessionStorage.getItem('user'));
    if (!user) return;
    const emailToUse = user.role === "admin" ? user.id : user.userId;

    // Fetch baseline contacts context
    axios.get(`https://botapi.emedha.in/pages/api/singleContact?user_id=${emailToUse}`)
      .then(response => setContacts(response.data))
      .catch(error => console.error('Error fetching baseline contacts:', error));

    // Dynamic Live Table Fetch System
    if (emailToUse) {
      axios.get('https://botapi.emedha.in/pages/api/api/tables', { params: { userId: emailToUse } })
        .then(res => {
          const groupsData = res.data;
          let filteredGroups;
          if (user.role === "admin") {
            filteredGroups = groupsData;
          } else if (user.role === "member") {
            filteredGroups = groupsData.filter(group => group.role === "member" && group.member_id === user.id);
          }
          setTableOptions(filteredGroups || []);

          if (filteredGroups && filteredGroups.length > 0) {
            const mockColumns = Object.keys(filteredGroups[0]).map(key => ({
              COLUMN_NAME: key,
              COLUMN_TYPE: "varchar"
            }));
            setTableColumns(mockColumns);
          }
        })
        .catch(err => console.error('Failed to fetch API managed structural tables:', err));
    }
  }, []);

  // Fetch Columns when table selection is changed
  useEffect(() => {
    if (!selectedTable) return;

    const user = JSON.parse(sessionStorage.getItem('user'));
    const emailToUse = user ? (user.role === "admin" ? user.id : user.userId) : null;

    axios.get(`https://botapi.emedha.in/pages/api/api/get-table-columns`, { params: { tableName: selectedTable, userId: emailToUse } })
      .then(res => {
        if (res.data && res.data.length > 0) {
          setTableColumns(res.data);
        }
      })
      .catch(() => {
        setTableColumns([
          { COLUMN_NAME: "name", COLUMN_TYPE: "varchar" },
          { COLUMN_NAME: "mobile", COLUMN_TYPE: "varchar" },
          { COLUMN_NAME: "cl_msg", COLUMN_TYPE: "varchar" }
        ]);
      });
  }, [selectedTable]);

  // Input Sanitization Handlers
  const handleNameChange = (e) => {
    // Allows text and spaces only
    const sanitizedValue = e.target.value.replace(/[^a-zA-Z\s]/g, "");
    setName(sanitizedValue);
  };

  const handleMobileChange = (e) => {
    // Allows numbers only, limits to a max length of 10 digits
    const sanitizedValue = e.target.value.replace(/[^0-9]/g, "");
    if (sanitizedValue.length <= 10) {
      setMobile(sanitizedValue);
    }
  };

  // API Action: Save Single Contact
  const handleAddContact = () => {
    const storedUser = JSON.parse(sessionStorage.getItem("user"));
    if (!storedUser || !storedUser.id) {
      alert("User not logged in");
      return;
    }
    if (!selectedTable) {
      alert("Please select a target destination table!");
      return;
    }
    if (!name || !mobile) {
      alert("Please enter both name and mobile.");
      return;
    }
    if (mobile.length !== 10) {
      alert("Mobile number must be exactly 10 digits.");
      return;
    }
    if (!selectedGroup2) {
      alert("Please select a group!");
      return;
    }
    if (!selectedSubgroup1) {
      alert("Please select a subgroup!");
      return;
    }

    const isAdmin = storedUser.role === "admin";
    const isMember = storedUser.role === "member";
    const adminId = isAdmin ? storedUser.id : storedUser.userId;
    const memberId = isMember ? storedUser.id : null;

    const payload = {
      table_name: selectedTable,
      name: name,
      mobile: mobile,
      group_id: Number(selectedGroup2),
      subgroup_id: Number(selectedSubgroup1),
      user_id: adminId,
      role: storedUser.role,
      flag: 1,
      cl_msg: "test"
    };

    if (isMember) {
      payload.member_id = memberId;
    }

    axios.post("https://botapi.emedha.in/pages/api/singleContact/add", payload)
      .then((response) => {
        setContacts([...contacts, response.data]);
        setName("");
        setMobile("");
        alert("Contact added successfully!");
      })
      .catch((error) => {
        console.error("Error adding contact:", error);
        if (error.response && error.response.data && error.response.data.error) {
          alert(error.response.data.error);
        } else {
          alert("Failed to add contact. Please check network logs or database column requirements.");
        }
      });
  };

  // Local file processing selector
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  // API Action: Process and Upload Bulk Contacts File
  const handleBulkSubmit = () => {
    const storedUser = JSON.parse(sessionStorage.getItem('user'));
    if (!storedUser || !storedUser.id) {
      alert('User not logged in.');
      return;
    }
    if (!selectedTable) {
      alert('Please select a table name before uploading.');
      return;
    }
    if (!selectedGroup2) {
      alert('Please select a target group from the list first.');
      return;
    }
    if (!selectedFile) {
      alert('Please choose an Excel file first before clicking upload.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const sheetData = XLSX.utils.sheet_to_json(sheet, { raw: false });

      if (sheetData.length === 0) {
        alert('Excel file is empty.');
        return;
      }

      const excelHeaders = Object.keys(sheetData[0]);
      const dbColumnNames = tableColumns.map(col => col.COLUMN_NAME);
      const excelToDbMap = {};

      // Change this block inside handleBulkSubmit:
      excelHeaders.forEach(header => {
        const normalizedHeader = header.toLowerCase().replace(/\s/g, '');

        // Use strict equality instead of .includes()
        const matchedDbCol = dbColumnNames.find(col =>
          col.toLowerCase().replace(/\s/g, '') === normalizedHeader
        );

        if (matchedDbCol) {
          excelToDbMap[header] = matchedDbCol;
        }
      });

      const filteredContacts = sheetData.map(row => {
        const contact = {};
        Object.entries(excelToDbMap).forEach(([excelKey, dbKey]) => {
          contact[dbKey] = row[excelKey] ?? null;
        });
        return contact;
      });

      const contactsWithMeta = filteredContacts.map(c => ({
        ...c,
        group_id: selectedGroup2,
        subgroup_id: selectedSubgroup1 || null,
        user_id: storedUser.id
      }));

      const adminId = storedUser.role === "admin" ? storedUser.id : storedUser.userId;
      const memberId = storedUser.role === "member" ? storedUser.id : null;

      axios.post('https://botapi.emedha.in/pages/api/uploadfile-contacts', {
        groupId: selectedGroup2,
        subgroupId: selectedSubgroup1 || "",
        tableName: selectedTable,
        contacts: contactsWithMeta,
        user_id: adminId,
        role: storedUser.role,
        member_id: memberId,
        email: storedUser.email,
        wa_id: storedUser.wa_id,
      })
        .then((response) => {
          if (response.data && response.data.message) {
            alert(response.data.message);
          } else {
            alert('Contacts uploaded successfully!');
          }
          setSelectedFile(null);
        })
        .catch((error) => {
          console.error('Error uploading contacts:', error);
          if (error.response && error.response.data && error.response.data.error) {
            alert(error.response.data.error);
          } else {
            alert('Failed to upload contacts. Verify fields are selected completely.');
          }
        });
    };

    reader.readAsArrayBuffer(selectedFile);
  };


  const handleDownloadDemo = () => {
    if (!tableColumns.length) {
      alert('Please select a table Name first');
      return;
    }

    const excludedColumns = [
      'id',
      'group_id',
      'subgroup_id',
      'user_id',
      'flag',
      'role',
      'member_id'
    ];

    const includedColumns = tableColumns.filter(
      col => !excludedColumns.includes(col.COLUMN_NAME)
    );

    // Set name and mobile first, then the remaining columns
    const priorityColumns = ['name', 'mobile'];

    const sortedColumns = [
      ...priorityColumns
        .map(name =>
          includedColumns.find(
            col => col.COLUMN_NAME.toLowerCase() === name
          )
        )
        .filter(Boolean),

      ...includedColumns.filter(
        col => !priorityColumns.includes(col.COLUMN_NAME.toLowerCase())
      )
    ];

    const sampleRow = {};

    sortedColumns.forEach(col => {
      if (col.COLUMN_NAME && col.COLUMN_TYPE?.toLowerCase().includes('date')) {
        const today = new Date();
        const formattedDate = `${today.getDate()}/${today.getMonth() + 1}/${today.getFullYear()}`;
        sampleRow[col.COLUMN_NAME] = formattedDate;
      } else {
        sampleRow[col.COLUMN_NAME] = '';
      }
    });

    const sample = [sampleRow];

    const worksheet = XLSX.utils.json_to_sheet(sample);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sample');
    XLSX.writeFile(workbook, 'sample_upload.xlsx');
  };

  const filteredSubgroups = subgroups1.filter(
    (subg) => String(subg.group_id) === String(selectedGroup2)
  );

  const handleReset = () => {
    setSelectedTable("");
    setSelectedGroup2("");
    setSelectedSubgroup1("");
    setName("");
    setMobile("");
    setSelectedFile(null);
  };

  return (
    <div style={{ fontFamily: "system-ui, sans-serif", backgroundColor: "#f8fafc", minHeight: "100vh" }}>
      {/* Header Layout */}
      <div style={{ marginBottom: "24px", display: "flex", alignItems: "center", gap: "10px" }}>
        <div style={{ backgroundColor: "#e0f2fe", borderRadius: "8px", display: "flex", padding: "8px", color: "#0369a1" }}>
          <Users size={20} />
        </div>
        <div>
          <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "700", color: "#1e293b" }}>Contact Management</h2>
          <p style={{ margin: 0, fontSize: "13px", color: "#64748b" }}>Configure details below and switch formats dynamically to save records.</p>
        </div>
      </div>

      <div style={{ maxWidth: "680px", margin: "0 auto", display: "flex", flexDirection: "column", gap: "24px" }}>

        {/* SHARED FILTERS CARD */}
        <div style={{ background: "#fff", padding: "20px", borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", border: "1px solid #e2e8f0" }}>

          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <LocalField
              label="Select Table Name"
              select
              options={tableOptions}
              required
              value={selectedTable}
              onChange={(e) => setSelectedTable(e.target.value)}
            />
            <LocalField
              label="Select Group"
              select
              options={groups2}
              required
              value={selectedGroup2}
              onChange={(e) => {
                setSelectedGroup2(e.target.value);
                setSelectedSubgroup1("");
              }}
            />
            <LocalField
              label="Select SubGroup"
              select
              options={filteredSubgroups}
              required
              value={selectedSubgroup1}
              onChange={(e) => setSelectedSubgroup1(e.target.value)}
            />

            {/* FORMAT TYPE TOGGLE (Radio Checkboxes) */}
            <div style={{ marginTop: "8px", paddingTop: "16px", borderTop: "1px solid #f1f5f9" }}>
              <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569", marginBottom: "8px", display: "block" }}>
                2. Select Creation Mode *
              </label>
              <div style={{ display: "flex", gap: "24px", alignItems: "center" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", fontWeight: "500", color: "#1e293b", cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="uploadMode"
                    checked={uploadMode === "single"}
                    onChange={() => setUploadMode("single")}
                    style={{ transform: "scale(1.15)", cursor: "pointer" }}
                  />
                  Add Contact
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", fontWeight: "500", color: "#1e293b", cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="uploadMode"
                    checked={uploadMode === "bulk"}
                    onChange={() => setUploadMode("bulk")}
                    style={{ transform: "scale(1.15)", cursor: "pointer" }}
                  />
                  Bulk Upload Contacts
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* DYNAMIC SECONDARY ACTION FORM */}
        <div style={{ background: "#fff", padding: "20px", borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", border: "1px solid #e2e8f0" }}>

          {uploadMode === "single" ? (
            /* --- SINGLE MANIFEST VIEW --- */
            <div>
              <h3 style={{ marginTop: 0, marginBottom: "16px", fontSize: "15px", color: "#1e293b" }}>3.Contact</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" }}>
                  <LocalField
                    label="Contact Name"
                    placeholder="Enter letters only" required value={name} onChange={handleNameChange} />
                  <LocalField
                    label="Mobile Number"
                    placeholder="10 digit number"
                    required
                    value={mobile}
                    onChange={handleMobileChange}
                  />
                </div>

                {selectedTable && (
                  <div style={{ fontSize: "12px", color: "#2563eb", backgroundColor: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "8px", padding: "10px", display: "flex", alignItems: "center", gap: "8px", fontWeight: "500" }}>
                    <FileSpreadsheet size={15} style={{ color: "#3b82f6", flexShrink: 0 }} />
                    <span>Format calibrated for: <strong style={{ fontWeight: "600" }}>{selectedTable}</strong></span>
                  </div>
                )}

                <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                  <button
                    onClick={handleAddContact}
                    style={{ width: "100%", padding: "10px", backgroundColor: "#2563eb", color: "#fff", border: "none", borderRadius: "6px", fontWeight: "600", cursor: "pointer", display: "flex", justifyContent: "center", alignItems: "center", gap: "6px" }}
                  >
                    <UserPlus size={14} /> Save Contact
                  </button>
                  <button
                    type="button"
                    onClick={handleReset}
                    style={{ padding: "10px 16px", backgroundColor: "#fff", color: "#334155", border: "1px solid #cbd5e1", borderRadius: "6px", fontWeight: "600", cursor: "pointer" }}
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* --- BULK MANIFEST VIEW --- */
            <div>
              <h3 style={{ marginTop: 0, marginBottom: "16px", fontSize: "15px", color: "#1e293b" }}>3. File Upload(Bulk Upload)</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

                {selectedTable ? (
                  <div style={{ fontSize: "12px", color: "#b45309", backgroundColor: "#fffbeb", border: "1px solid #fde68a", borderRadius: "8px", padding: "12px", display: "flex", flexDirection: "column", gap: "6px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: "600" }}>
                      <FileSpreadsheet size={16} style={{ color: "#f59e0b", flexShrink: 0 }} />
                      <span>selected Table: {selectedTable}</span>
                    </div>
                    <p style={{ color: "#475569", fontSize: "11px", margin: 0 }}>
                      Columns match the schema of this specific table. Max 500 contacts per upload session.
                    </p>
                    <button
                      type="button"
                      onClick={handleDownloadDemo}
                      style={{ textDecoration: "underline", fontWeight: "bold", color: "#92400e", marginTop: "4px", textAlign: "left", display: "flex", alignItems: "center", gap: "4px", backgroundColor: "transparent", border: "0", padding: "0", cursor: "pointer", outline: "none" }}
                    >
                      ↓ Download Demo Excel for {selectedTable}
                    </button>

                    {/* THREE LINES INSTRUCTIONS CONTAINER */}
                    <div style={{ marginTop: "10px", paddingTop: "10px", borderTop: "1px dashed #fcd34d", color: "#78350f", fontSize: "11px" }}>
                      <p style={{ margin: "0 0 4px 0", fontWeight: "600" }}>Instructions:</p>
                      <ol style={{ margin: 0, paddingLeft: "16px" }}>
                        <li style={{ marginBottom: "2px" }}>Download the Template file and fill it with proper data.</li>
                        <li style={{ marginBottom: "2px" }}>Don’t change the header column in the table.</li>
                        <li>Just save the file and upload the same in the upload option.</li>
                      </ol>
                    </div>

                  </div>
                ) : (
                  <div style={{ fontSize: "12px", color: "#64748b", backgroundColor: "#f8fafc", border: "1px dashed #e2e8f0", borderRadius: "8px", padding: "12px", textAlign: "center" }}>
                    Please select a baseline table schema context above to fetch its demo Excel layout template.
                  </div>
                )}

                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>
                    Upload (Excel File)
                  </label>
                  <input
                    type="file"
                    accept=".xlsx, .xls"
                    disabled={!selectedTable}
                    onChange={handleFileChange}
                    style={{ width: "100%", fontSize: "12px", color: "#64748b", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "4px", backgroundColor: "#ffffff", opacity: !selectedTable ? 0.6 : 1, cursor: !selectedTable ? "not-allowed" : "default" }}
                  />
                </div>

                <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                  <button
                    type="button"
                    onClick={handleBulkSubmit}
                    disabled={!selectedTable || !selectedFile}
                    style={{ flex: "1", padding: "10px", backgroundColor: (!selectedTable || !selectedFile) ? "#94a3b8" : "#2563eb", color: "#fff", border: "none", borderRadius: "6px", fontWeight: "600", cursor: (!selectedTable || !selectedFile) ? "not-allowed" : "pointer", display: "flex", justifyContent: "center", alignItems: "center", gap: "6px" }}
                  >
                    <Upload size={14} /> Upload
                  </button>
                  <button
                    type="button"
                    onClick={handleReset}
                    style={{ padding: "10px 16px", backgroundColor: "#fff", color: "#334155", border: "1px solid #cbd5e1", borderRadius: "6px", fontWeight: "600", cursor: "pointer" }}
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}